import { cva } from 'class-variance-authority'

import { TextSize } from '#components/text/types'

import { InputSize, InputSlotPosition } from './types'

export const inputSizeToTextSize = {
  [InputSize.Small]: TextSize.Small,
  [InputSize.Medium]: TextSize.Medium,
  [InputSize.Large]: TextSize.Medium,
} as const

/**
 * CVA variants for the input container (wraps input + prefix/suffix).
 *
 * Rounded corners, focus-within ring, and invalid focus ring all live in the
 * base. When used inside an InputGroup the wrapper overrides these via
 * descendant selectors - no variant gating needed.
 */
export const inputContainerVariants = cva(
  [
    'flex items-center overflow-clip',
    'bg-surface-controls-default',
    'border-[0.5px] border-border-field-default rounded-md',
    'shadow-xs',
    'transition-[border,box-shadow] duration-175 ease',
    'hover:border-border-field-hover',
    'focus-within:shadow-[0_0_0_2.5px_var(--color-shadow-focus-ring-default)] focus-within:border-border-focused',
    // Invalid (aligns with Base UI data-invalid attribute)
    'data-[invalid]:border-border-danger-strong',
    'data-[invalid]:focus-within:shadow-[0_0_0_2.5px_var(--color-shadow-focus-ring-danger)]',
    // Disabled
    'data-[disabled=true]:bg-surface-disabled data-[disabled=true]:border-border-disabled data-[disabled=true]:opacity-disabled',
    'data-[disabled=true]:cursor-not-allowed data-[disabled=true]:hover:border-border-disabled',
    'data-[disabled=true]:[&_*]:pointer-events-none',
    // Internal input styles
    '[&_:where(input)]:h-full',
  ],
  {
    defaultVariants: {
      size: InputSize.Medium,
    },
    variants: {
      size: {
        [InputSize.Small]: 'min-h-6',
        [InputSize.Medium]: 'min-h-7',
        [InputSize.Large]: 'min-h-8',
      },
    },
  },
)

/**
 * Padding classes applied to the input element via CSS child selectors
 */
export const inputPaddingClasses = {
  [InputSize.Small]: {
    left: '[&_:where(input)]:pl-2',
    right: '[&_:where(input)]:pr-2',
  },
  [InputSize.Medium]: {
    left: '[&_:where(input)]:pl-2.5',
    right: '[&_:where(input)]:pr-2.5',
  },
  [InputSize.Large]: {
    left: '[&_:where(input)]:pl-2.5',
    right: '[&_:where(input)]:pr-2.5',
  },
} as const

/**
 * Base classes for the input element (non-typography styles).
 * Typography is handled separately via textVariants from the text component.
 */
export const inputBaseClasses = [
  'flex-1 w-full min-w-0',
  'bg-transparent border-none outline-none',
  'placeholder:text-text-tertiary',
  'disabled:cursor-not-allowed',
]

/**
 * CVA variants for prefix/suffix slots.
 * Slots are square when containing only SVG, otherwise use horizontal padding.
 */
export const inputSlotVariants = cva(
  [
    'flex items-center justify-center shrink-0',
    '[&:has(>svg:only-child)]:px-0',
    // Icon size (14px)
    '[&>svg]:size-3.5',
  ],
  {
    defaultVariants: {
      size: InputSize.Medium,
      position: InputSlotPosition.Right,
    },
    variants: {
      size: {
        // Square sizing when SVG only, horizontal padding otherwise
        [InputSize.Small]: 'px-2 [&:has(>svg:only-child)]:size-5.5',
        [InputSize.Medium]: 'px-2.5 [&:has(>svg:only-child)]:size-6.5',
        [InputSize.Large]: 'px-2.5 [&:has(>svg:only-child)]:size-7.5',
      },
      position: {
        [InputSlotPosition.Left]: 'mr-0.25',
        [InputSlotPosition.Right]: '',
      },
    },
  },
)
